import {
  CLEO_ACCENT_CHROMA,
  CLEO_ACCENT_LIGHTNESS,
  CLEO_CSS_VARIABLES,
  resolveCleoAccentForWorld,
  type CleoAccentForWorld,
} from "./contract";
import { CLEO_IDENTITY_CSS_VARIABLES } from "./cleo-identity";

export const DEFAULT_WORLD_ACCENT_HUE = 55;
export const DEFAULT_WORLD_ACCENT_CHROMA = 0.1;
export const MIN_WORLD_ACCENT_CHROMA = 0.02;
export const MAX_WORLD_ACCENT_CHROMA = 0.16;
export const WORLD_ACCENT_LIGHTNESS = 0.71;

export interface WorldThemeProfile {
  readonly accentHue: number;
  readonly accentChroma: number;
}

export const DEFAULT_WORLD_THEME_PROFILE: WorldThemeProfile = Object.freeze({
  accentHue: DEFAULT_WORLD_ACCENT_HUE,
  accentChroma: DEFAULT_WORLD_ACCENT_CHROMA,
});

export type ResolvedAppearance = "light" | "dark";

export const WORLD_THEME_CSS_VARIABLES = Object.freeze({
  accentHue: "--ncl-world-accent-hue",
  accentChroma: "--ncl-world-accent-chroma",
  accent: "--ncl-world-accent",
  accentForeground: "--ncl-world-accent-foreground",
  accentSurface: "--ncl-world-accent-surface",
  accentSurfaceForeground: "--ncl-world-accent-surface-foreground",
} as const);

export type WorldThemeCssVariable =
  (typeof WORLD_THEME_CSS_VARIABLES)[keyof typeof WORLD_THEME_CSS_VARIABLES];

export interface ResolvedWorldTheme {
  readonly profile: WorldThemeProfile;
  readonly appearance: ResolvedAppearance;
  /**
   * The World's accent variables — plus, ONLY when the World sits inside
   * Cleo's band, her yielded channel and identity overrides (LG11): the
   * fixed :root seat stands everywhere else, and the mark + its field never
   * shift (the mark's contrast rides its own field, not hue distance).
   */
  readonly cssVariables: Readonly<Record<string, string>>;
  readonly cleoAccent: CleoAccentForWorld;
}

function finiteNumber(value: unknown, fallback: number): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function record(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : {};
}

function compactNumber(value: number): string {
  return String(Math.round(value * 100_000) / 100_000);
}

function oklch(lightness: number, chroma: number, hue: number): string {
  return `oklch(${compactNumber(lightness)} ${compactNumber(chroma)} ${compactNumber(hue)})`;
}

/** Wrap any finite hue onto the half-open [0, 360) color wheel. */
export function wrapWorldAccentHue(value: unknown): number {
  const hue = finiteNumber(value, DEFAULT_WORLD_ACCENT_HUE);
  return ((hue % 360) + 360) % 360;
}

/** Clamp chroma to the contrast-tested World accent envelope. */
export function clampWorldAccentChroma(value: unknown): number {
  const chroma = finiteNumber(value, DEFAULT_WORLD_ACCENT_CHROMA);
  return Math.min(MAX_WORLD_ACCENT_CHROMA, Math.max(MIN_WORLD_ACCENT_CHROMA, chroma));
}

/** Parse persisted or untrusted data into the only fields a World owns. */
export function parseWorldThemeProfile(value: unknown): WorldThemeProfile {
  const source = record(value);
  return {
    accentHue: wrapWorldAccentHue(source.accentHue),
    accentChroma: clampWorldAccentChroma(source.accentChroma),
  };
}

/**
 * Deterministically derive a display palette. Only the profile is persisted;
 * these colors are recalculated for the viewer's resolved appearance.
 */
export function resolveWorldTheme(
  value: unknown,
  appearance: ResolvedAppearance = "light",
): ResolvedWorldTheme {
  const profile = parseWorldThemeProfile(value);
  const { accentHue: hue, accentChroma: chroma } = profile;
  const resolvedAppearance: ResolvedAppearance = appearance === "dark" ? "dark" : "light";
  const dark = resolvedAppearance === "dark";
  const surfaceChroma = Math.max(MIN_WORLD_ACCENT_CHROMA, chroma * (dark ? 0.38 : 0.22));

  const cleoAccent = resolveCleoAccentForWorld(hue);

  return {
    profile,
    appearance: resolvedAppearance,
    cleoAccent,
    cssVariables: Object.freeze({
      [WORLD_THEME_CSS_VARIABLES.accentHue]: compactNumber(hue),
      [WORLD_THEME_CSS_VARIABLES.accentChroma]: compactNumber(chroma),
      [WORLD_THEME_CSS_VARIABLES.accent]: oklch(WORLD_ACCENT_LIGHTNESS, chroma, hue),
      [WORLD_THEME_CSS_VARIABLES.accentForeground]: oklch(0.18, MIN_WORLD_ACCENT_CHROMA, hue),
      [WORLD_THEME_CSS_VARIABLES.accentSurface]: oklch(dark ? 0.28 : 0.94, surfaceChroma, hue),
      [WORLD_THEME_CSS_VARIABLES.accentSurfaceForeground]: oklch(
        dark ? 0.92 : 0.24,
        MIN_WORLD_ACCENT_CHROMA,
        hue,
      ),
      // LG11 — Cleo yields: her interface family re-shades at the yielded
      // DATED 2026-09-02 (the orange re-articulation): the yielded channel
      // reads CLEO_ACCENT_LIGHTNESS, the seat's own, where it hard-coded the
      // teal era's 0.636 — a yield must not silently re-light her. The 0.82
      // and 0.35 shades stay this law's own yielded ramp (named absence: they
      // are not derived from the family's light/deep).
      // hue (the fixed seat's ramp lightnesses), only while this World sits
      // inside her band. The mark and its field never shift.
      ...(cleoAccent.yielded
        ? {
            [CLEO_CSS_VARIABLES.hue]: compactNumber(cleoAccent.hue),
            [CLEO_CSS_VARIABLES.accent]: oklch(CLEO_ACCENT_LIGHTNESS, CLEO_ACCENT_CHROMA, cleoAccent.hue),
            [CLEO_CSS_VARIABLES.accentLight]: oklch(0.82, CLEO_ACCENT_CHROMA, cleoAccent.hue),
            [CLEO_CSS_VARIABLES.accentDeep]: oklch(0.35, CLEO_ACCENT_CHROMA, cleoAccent.hue),
            [CLEO_IDENTITY_CSS_VARIABLES.identity]: oklch(0.82, CLEO_ACCENT_CHROMA, cleoAccent.hue),
            [CLEO_IDENTITY_CSS_VARIABLES.identityDim]: oklch(CLEO_ACCENT_LIGHTNESS, CLEO_ACCENT_CHROMA, cleoAccent.hue),
            [CLEO_IDENTITY_CSS_VARIABLES.identityEdge]: oklch(0.35, CLEO_ACCENT_CHROMA, cleoAccent.hue),
          }
        : {}),
    }),
  };
}

/*
 * The write law — dated re-articulation 2026-08-31 (LG11), superseding the
 * 2026-08-29 reserved-band refusal at the principal's word: a World accent
 * write NEVER refuses a hue. The full wheel is the person's; wrap and clamp
 * exactly as reads do, and report how Cleo yields so the door's answer can
 * say it out loud. The old refusal vocabulary is deleted, never overloaded
 * (DL1).
 */
export interface WorldAccentWriteResolution {
  readonly profile: WorldThemeProfile;
  readonly cleoAccent: CleoAccentForWorld;
}

export function resolveWorldAccentWrite(value: unknown): WorldAccentWriteResolution {
  const profile = parseWorldThemeProfile(value);
  return { profile, cleoAccent: resolveCleoAccentForWorld(profile.accentHue) };
}
